/**
 * Mobile bridge — runs only when the React app is running inside the
 * Capacitor native shell (iOS + Android). On the web, every function here
 * is a no-op so we ship a single codebase.
 *
 * What it does:
 *   1. Registers the device for push notifications, sends the device token
 *      to our backend, and posts a `gaTrack` event so we have install funnels.
 *   2. Routes Universal Link taps (`thecauldron.app/r/{code}`,
 *      `cauldron.app/ambassadors`, etc.) into React Router instead of
 *      bouncing out to Safari/Chrome.
 *   3. Exposes a tiny `nativeShare()` helper so the Ambassador "Copy link"
 *      button can use the OS share sheet on mobile and falls back to
 *      clipboard on web.
 *   4. Handles "back button" on Android sensibly (navigate back if there's
 *      history, otherwise minimize the app instead of nuking the process).
 */
import { api } from "./api";

let _isNative = false;
let _platform = "web";

export const isNative = () => _isNative;
export const getPlatform = () => _platform;

export async function initMobile(navigate) {
  // Lazy-import Capacitor so the web bundle isn't bloated when not running natively.
  let Capacitor;
  try {
    ({ Capacitor } = await import("@capacitor/core"));
  } catch {
    return; // Capacitor not installed (older build, web-only env)
  }
  _isNative = Capacitor?.isNativePlatform?.() === true;
  if (!_isNative) return;
  _platform = Capacitor.getPlatform();

  // ─── Universal / App Links → React Router ──────────────────────────────
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("appUrlOpen", (event) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search + url.hash;
        if (path && path !== "/") navigate(path);
      } catch { /* malformed deep link — ignore */ }
    });
    // Android hardware back button — pop history, minimize if at root.
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.minimizeApp?.();
      }
    });
  } catch { /* @capacitor/app not installed yet (CI build) */ }

  // ─── Push Notifications ────────────────────────────────────────────────
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.checkPermissions();
    let granted = perm.receive === "granted";
    if (!granted) {
      const req = await PushNotifications.requestPermissions();
      granted = req.receive === "granted";
    }
    if (granted) {
      await PushNotifications.register();
      PushNotifications.addListener("registration", async (token) => {
        try {
          await api.post("/devices/register", {
            platform: _platform,
            token: token.value,
            bundleId: "app.cauldron",
          });
        } catch { /* user might not be logged in yet — backend route accepts anon */ }
      });
      // Tapped a notification → deep-link to the relevant in-app screen.
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        const path = action?.notification?.data?.path;
        if (path) navigate(path);
      });
    }
  } catch { /* push plugin missing */ }
}

/**
 * Native share sheet on iOS/Android, clipboard fallback on web.
 */
export async function nativeShare(opts) {
  if (_isNative) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({
        title: opts.title || "Cauldron",
        text: opts.text,
        url: opts.url,
        dialogTitle: opts.title || "Share",
      });
      return { method: "native" };
    } catch { /* user cancelled / share unavailable */ }
  }
  // Web — try the Web Share API first (modern Chrome / Safari mobile-web)
  try {
    if (navigator.share) {
      await navigator.share({ title: opts.title, text: opts.text, url: opts.url });
      return { method: "web-share" };
    }
  } catch { /* user dismissed */ }
  // Final fallback — clipboard
  try {
    await navigator.clipboard.writeText(opts.url || opts.text);
    return { method: "clipboard" };
  } catch {
    return { method: "failed" };
  }
}
